/**
 * Saudi banking helpers — banks, regions, transfer rails (SARIE/SADAD/SWIFT), amounts.
 */
import {
  formatBrandMoney,
  formatBrandMoneyCompact,
  getBrandCurrencySuffix,
  getBrandDefaultCurrency,
  getBrandLocale,
  getBrandVatRate,
} from './brandCurrency';
import {
  formatSaudiIbanDisplay,
  normalizeSaudiIban,
  SAUDI_IBAN_EXAMPLE,
  SAUDI_IBAN_PLACEHOLDER,
  validateSaudiIban,
} from './ibanValidation';

export {
  formatSaudiIbanDisplay,
  normalizeSaudiIban,
  SAUDI_IBAN_EXAMPLE,
  SAUDI_IBAN_PLACEHOLDER,
  validateSaudiIban,
};

/** Approved Saudi commercial banks — `code` = short code, `swift` = BIC/SWIFT */
export const SAUDI_BANKS = [
  { code: 'SNB', swift: 'NCBKSAJE', name: 'البنك الأهلي السعودي (SNB)' },
  { code: 'RJHI', swift: 'RJHISARI', name: 'مصرف الراجحي (Al Rajhi)' },
  { code: 'RIBL', swift: 'RIBLSARI', name: 'بنك الرياض (Riyad Bank)' },
  { code: 'INMA', swift: 'INMASARI', name: 'مصرف الإنماء (Alinma)' },
  { code: 'SAIB', swift: 'SIBCSARI', name: 'البنك السعودي للاستثمار (SAIB)' },
  { code: 'BSFR', swift: 'BSFRSARI', name: 'البنك السعودي الفرنسي (BSF)' },
  { code: 'BLAD', swift: 'ALBISARI', name: 'بنك البلاد (Bank AlBilad)' },
  { code: 'SABB', swift: 'SABBSARI', name: 'بنك ساب (SABB / HSBC Saudi)' },
  { code: 'BJAZ', swift: 'BJAZSAJE', name: 'بنك الجزيرة (Bank AlJazira)' },
  { code: 'ANB', swift: 'ARNBSARI', name: 'البنك العربي الوطني (ANB)' },
];

export const SAUDI_PRIMARY_BANK_CODES = ['SNB', 'RJHI', 'RIBL', 'INMA'];

export const SAUDI_BANK_CODES = SAUDI_BANKS.map((b) => b.code);

/** Saudi administrative regions */
export const SAUDI_REGIONS = [
  { code: '01', name: 'الرياض' },
  { code: '02', name: 'مكة المكرمة' },
  { code: '03', name: 'المدينة المنورة' },
  { code: '04', name: 'المنطقة الشرقية' },
  { code: '05', name: 'القصيم' },
  { code: '06', name: 'عسير' },
  { code: '07', name: 'تبوك' },
  { code: '08', name: 'حائل' },
  { code: '09', name: 'الحدود الشمالية' },
  { code: '10', name: 'جazan' },
  { code: '11', name: 'نجران' },
  { code: '12', name: 'الباحة' },
  { code: '13', name: 'الجوف' },
];

/** Saudi payment / transfer rails */
export const SAUDI_TRANSFER_SYSTEMS = [
  { value: 'sarie', labelAr: 'سريع (SARIE) — تحويل فوري بين البنوك' },
  { value: 'sadad', labelAr: 'سداد (SADAD) — سداد الفواتير والمدفوعات' },
  { value: 'swift', labelAr: 'سويفت (SWIFT) — تحويل دولي' },
  { value: 'internal', labelAr: 'تحويل داخلي بين حسابات الشركة' },
];

export const SAUDI_BALANCE_TIERS = {
  over2m: 2_000_000,
  over1m: 1_000_000,
  over500k: 500_000,
};

export const SAUDI_TRANSFER_AMOUNT_TIERS = {
  gt1m: 1_000_000,
  midMin: 500_000,
};

/** Default transfer limits in SAR */
export const SAUDI_DEFAULT_LIMITS = {
  maxTransferAmount: 5_000_000,
  requireApprovalAbove: 500_000,
  maxCheckAmount: 500_000,
  feeThreshold: 25,
};

export function formatBankMoney(amount, showSymbol = true) {
  return formatBrandMoney(amount, showSymbol);
}

export function formatBankMoneyCompact(amount) {
  return formatBrandMoneyCompact(amount);
}

export function getBankCurrencySuffix() {
  return getBrandCurrencySuffix();
}

export function getBankDefaultCurrency() {
  return getBrandDefaultCurrency();
}

export function getBankLocale(language) {
  return getBrandLocale(language);
}

export function bankVatFromAmount(amount) {
  const n = Math.abs(Number(amount) || 0);
  return n * (getBrandVatRate() / 100);
}

/** Match bank code, SWIFT, or Arabic name fragment */
export function bankMatchesCode(bankLabel, code) {
  const label = String(bankLabel || '').toUpperCase();
  if (!label) return false;
  const bank = SAUDI_BANKS.find((b) => b.code === code || b.swift === code);
  if (label.includes(code.toUpperCase())) return true;
  if (bank) {
    if (bank.swift && label.includes(bank.swift)) return true;
    if (label.includes(bank.code)) return true;
    const namePart = bank.name.split('(')[0].trim();
    if (namePart && label.includes(namePart)) return true;
  }
  return false;
}

/** Build account dropdown options from API accounts */
export function buildBankAccountOptions(accounts = [], p) {
  return (accounts || []).map((a) => {
    const label = `${a.bank_name || a.bankName || ''} — ${a.account_name || a.name || a.account_number || ''}`.trim();
    return { value: label, label, accountId: a.id, isDefault: true };
  });
}

/** Saudi bank quick-select options for transfer forms */
export function getSaudiTransferBankOptions(p) {
  return [
    ...SAUDI_BANKS.map((bank) => ({
      value: bank.shortCode,
      label: p?.(`bank_${bank.shortCode.toLowerCase()}`) || bank.name,
      isDefault: true,
    })),
    { value: 'external', label: p?.('bank_external') || 'حساب خارجي', isDefault: true },
  ];
}

export function getSaudiStatementPeriods() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const monthsAr = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
  ];
  const current = `${monthsAr[month]} ${year}`;
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const previous = `${monthsAr[prevMonth]} ${prevYear}`;
  const prev2Month = prevMonth === 0 ? 11 : prevMonth - 1;
  const prev2Year = prevMonth === 0 ? prevYear - 1 : prevYear;
  const twoMonthsAgo = `${monthsAr[prev2Month]} ${prev2Year}`;
  return [
    { value: current, label: current },
    { value: previous, label: previous },
    { value: twoMonthsAgo, label: twoMonthsAgo },
  ];
}

export const SAUDI_DEFAULT_BANK_ACCOUNTS = [
  { code: 'RJHI', name: 'مصرف الراجحي', iban: 'SA0380000000608010167519' },
  { code: 'SNB', name: 'البنك الأهلي السعودي', iban: 'SA0380000000608010187519' },
  { code: 'RIBL', name: 'بنك الرياض', iban: 'SA6820000000608010234567' },
  { code: 'INMA', name: 'مصرف الإنماء', iban: 'SA0510000000608010456789' },
];

/** Flat UI config defaults for Saudi banking module */
export function buildDefaultBankingConfig() {
  return {
    defaultBankAccount: '',
    autoImportStatements: true,
    statementImportFrequency: 'daily',
    maxTransferAmount: SAUDI_DEFAULT_LIMITS.maxTransferAmount,
    requireApprovalAbove: SAUDI_DEFAULT_LIMITS.requireApprovalAbove,
    transferApprovalLevels: 2,
    allowInternationalTransfers: true,
    checkClearanceDays: 3,
    alertExpiringChecks: true,
    expiringChecksDays: 7,
    maxCheckAmount: SAUDI_DEFAULT_LIMITS.maxCheckAmount,
    autoExecuteOrders: false,
    orderExecutionTime: '09:00',
    requireDualApproval: true,
    autoReconciliation: true,
    reconciliationTolerance: 1000,
    alertOnMismatch: true,
    reconciliationFrequency: 'daily',
    trackBankFees: true,
    alertOnHighFees: true,
    feeThreshold: SAUDI_DEFAULT_LIMITS.feeThreshold,
    requireTwoFactorAuth: false,
    sessionTimeout: 30,
    encryptData: true,
    logAllOperations: true,
    notifyOnTransfer: true,
    notifyOnCheckCleared: true,
    notifyOnPaymentOrder: true,
    notifyOnFees: true,
    emailNotifications: true,
    smsNotifications: false,
    defaultView: 'dashboard',
    showBalanceChart: true,
    itemsPerPage: 50,
    transferReferencePrefix: 'TRF',
    checkReferencePrefix: 'CHQ',
    statementReferencePrefix: 'BST',
    accountReferencePrefix: 'BAC',
    entryReferencePrefix: 'BJE',
    dateFormat: 'DD/MM/YYYY',
    defaultCurrency: getBrandDefaultCurrency(),
    lowBalanceThreshold: 10_000,
    highTransactionThreshold: 500_000,
    unusualActivityThreshold: 1_000_000,
    enabledTransferSystems: ['sarie', 'sadad'],
    sarieInstantTransfer: true,
    sadadBillPayment: true,
    bankApiIntegration: false,
    statementImportFormat: 'csv',
    defaultBankCode: 'RJHI',
  };
}
